import React, { useEffect, useMemo, useState } from "react";
import { DeleteOutlined, EditOutlined, LoadingOutlined, PlusOutlined, SearchOutlined, TagsOutlined } from "@ant-design/icons";
import adminApi from "../api/adminApi";
import ConfirmDialog from "./ConfirmDialog";

const blank = { name: "", description: "" };

export default function CategoriesAdmin() {
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    try {
      const [categoryData, projectData] = await Promise.all([adminApi.getProjectCategories(), adminApi.getProjects()]);
      setCategories(categoryData || []);
      setProjects(projectData || []);
    } catch (err) { setError(adminApi.getErrorMessage(err, "Could not load categories")); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => categories.filter((item) => `${item.name} ${item.description}`.toLowerCase().includes(query.toLowerCase())), [categories, query]);

  function startEdit(category) {
    setEditing(category);
    setForm({ name: category.name, description: category.description || "" });
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function cancelEdit() { setEditing(null); setForm(blank); }

  async function submit(event) {
    event.preventDefault();
    if (!form.name.trim()) return setError("Category name is required.");
    setSaving(true); setError("");
    try {
      if (editing) await adminApi.updateProjectCategory(editing._id, { name: form.name.trim(), description: form.description.trim() });
      else await adminApi.createProjectCategory({ name: form.name.trim(), description: form.description.trim() });
      cancelEdit();
      await load();
    } catch (err) { setError(adminApi.getErrorMessage(err, "Category could not be saved")); }
    finally { setSaving(false); }
  }

  async function confirmDelete() {
    const category = deleting;
    setDeleting(null);
    try {
      await adminApi.deleteProjectCategory(category._id);
      setCategories((items) => items.filter((item) => item._id !== category._id));
    } catch (err) { setError(adminApi.getErrorMessage(err, "Category could not be deleted")); }
  }

  return (
    <div className="admin-category-layout">
      <section className="admin-panel admin-category-form">
        <div className="admin-section-heading"><span>{editing ? <EditOutlined /> : <PlusOutlined />}</span><div><h3>{editing ? "Edit category" : "Create category"}</h3><p>Categories become project types in the portfolio.</p></div></div>
        {error && <div className="admin-alert error">{error}</div>}
        <form onSubmit={submit}>
          <label className="admin-field"><span>Category name <b>*</b></span><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Residential" required /></label>
          <label className="admin-field"><span>Description</span><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Briefly explain this project type" rows="4" /></label>
          <button className="admin-primary-button" disabled={saving}>{saving ? <><LoadingOutlined spin /> Saving…</> : editing ? "Save changes" : "Create category"}</button>
          {editing && <button type="button" className="admin-secondary-button" onClick={cancelEdit}>Cancel</button>}
        </form>
      </section>
      <section className="admin-panel admin-category-list">
        <div className="admin-panel-header"><div><h3>Project categories</h3><p>{categories.length} categories available</p></div></div>
        <div className="admin-search"><SearchOutlined /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search categories…" /></div>
        {loading ? <div className="admin-skeleton table" /> : filtered.length ? <div className="admin-category-rows">
          {filtered.map((category) => {
            const usage = projects.filter((project) => project.type === category.name).length;
            return <article key={category._id}><div className="admin-category-icon"><TagsOutlined /></div><div><strong>{category.name}</strong><p>{category.description || "No description provided"}</p></div><span className="admin-usage">{usage} project{usage === 1 ? "" : "s"}</span><div className="admin-row-actions"><button onClick={() => startEdit(category)} aria-label={`Edit ${category.name}`}><EditOutlined /></button><button onClick={() => setDeleting({ ...category, usage })} aria-label={`Delete ${category.name}`}><DeleteOutlined /></button></div></article>;
          })}
        </div> : <div className="admin-empty compact"><TagsOutlined /><h3>No matching categories</h3><p>Create a category or adjust your search.</p></div>}
      </section>
      <ConfirmDialog open={Boolean(deleting)} title={`Delete “${deleting?.name}”?`} description={deleting?.usage ? `${deleting.usage} existing project(s) will keep the text “${deleting.name}”, but it will no longer be selectable for new projects.` : "This category will be permanently removed."} confirmLabel="Delete category" onCancel={() => setDeleting(null)} onConfirm={confirmDelete} />
    </div>
  );
}
